import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, loginWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleCallback = useCallback(async (response) => {
    try {
      setLoading(true);
      await loginWithGoogle(response.credential);
    } catch (error) {
      console.error('Erro ao fazer login com Google:', error);
      toast.error(error.response?.data?.message || 'Erro ao fazer login com Google.');
    } finally {
      setLoading(false);
    }
  }, [loginWithGoogle]);

  // Carregar Google Sign-In script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID.apps.googleusercontent.com',
          callback: handleGoogleCallback,
          ux_mode: 'popup',
        });
        
        // Renderizar botão do Google (funciona em incognito)
        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          window.google.accounts.id.renderButton(
            buttonDiv,
            { 
              theme: 'outline', 
              size: 'large',
              width: buttonDiv.offsetWidth,
              text: 'continue_with',
              locale: 'pt-BR'
            }
          );
        }
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [handleGoogleCallback]);

  // Não precisa mais - botão é renderizado pelo Google SDK

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      await login(email, password);
    } catch (error) {
      console.error('Erro ao fazer login:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-600 via-tech-600 to-health-600 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="/logo-audfast.png" 
            alt="AudFast - Velocidade e Precisão em Cada Decibel" 
            className="h-24 object-contain mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gradient mb-2">Bem-vindo ao AudFast</h1>
          <p className="text-gray-600">Entre para acessar a plataforma</p>
        </div>

        <div className="space-y-4">
          {/* Google Login Button - Renderizado pelo SDK */}
          <div 
            id="google-signin-button" 
            className="w-full flex justify-center"
          ></div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">ou</span>
            </div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                E-mail
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="seu.email@exemplo.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Senha
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                  className="rounded text-medical-600 focus:ring-medical-500 disabled:opacity-50" 
                />
                <span className="ml-2 text-gray-600">Lembrar-me</span>
              </label>
              <button type="button" className="text-medical-600 hover:underline">
                Esqueceu a senha?
              </button>
            </div>

            <button 
              type="submit"
              disabled={loading}
              className="btn-primary w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Não tem uma conta?{' '}
            <Link to="/register" className="text-medical-600 hover:underline font-semibold">
              Criar conta gratuita
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-medical-600 transition-colors">
            ← Voltar para Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
